/*
 * Copyright (c) 2022 Robert J Whelton. All rights reserved.
 */

import * as React from "react"
import {useTheme} from "@mui/material/styles"
import Box from "@mui/material/Box"
import Toolbar from "@mui/material/Toolbar"
import AddIcon from "@mui/icons-material/Add"
import ShareIcon from "@mui/icons-material/IosShare"
import EditIcon from "@mui/icons-material/Edit"
import useMediaQuery from "@mui/material/useMediaQuery"
import Zoom from "@mui/material/Zoom"
import Fab from "@mui/material/Fab"
import {navigate} from "gatsby"
import Helmet from "react-helmet"

import Header from "./Header"
import NavigatorDrawer from "../components/NavigatorDrawer"
import {
  mobileDrawerWidthFactor,
  standardWidthFactor,
  enterSpeedFactor,
  exitSpeedFactor,
  standardMinWidth
} from "../components/BaseDrawer"
import SettingsDrawer from "../components/SettingsDrawer"
import AddNodeDrawer from "../components/AddNodeDrawer"
import ShareNodeDrawer from "../components/ShareNodeDrawer"
import Footer from "./Footer"

interface LayoutProps {
  children?: any
}

export type ToggleFunctionType = (open: boolean) => any

const Layout: React.FC<LayoutProps> = ({children}) => {
  const theme = useTheme()
  const [navigatorOpen, setNavigatorOpen] = React.useState(false)
  const [settingsMenuOpen, setSettingsMenuOpen] = React.useState(false)
  const [addNodeOpen, setAddNodeOpen] = React.useState(false)
  const [shareNodeOpen, setShareNodeOpen] = React.useState(false)
  const [title, setTitle] = React.useState("")
  const [copyrightOwnership, setCopyrightOwnership] = React.useState("")
  const notMobile = useMediaQuery(theme.breakpoints.up("sm"))
  const drawerWidth = (notMobile ? `${standardWidthFactor}%` : `${mobileDrawerWidthFactor}vw`)

  const toggle = (setState: React.Dispatch<React.SetStateAction<boolean>>) => (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent): void => {
    if (!(
      event && event.type === "keydown"
      && ((event as React.KeyboardEvent).key === "Tab"
        || (event as React.KeyboardEvent).key === "Shift")
    )) setState(!open)
  }

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen,
  }

  return (
    <>
      <Helmet>
        {/*hacked because I can't find where this is being set in gatsby vs material UI*/}
        <style>{`
        body { background-color: ${theme.palette.background.paper}; color: ${theme.palette.text.primary}; }
        a {color: #2288ffff;}
        `}</style>
      </Helmet>
      <Header
        navigatorOpen={navigatorOpen} toggleNavigator={toggle(setNavigatorOpen)}
        settingsMenuOpen={settingsMenuOpen} toggleSettingsMenu={toggle(setSettingsMenuOpen)}
        title={title}
      />
      <NavigatorDrawer open={navigatorOpen} toggleDrawer={toggle(setNavigatorOpen)}/>
      <SettingsDrawer open={settingsMenuOpen} toggleDrawer={toggle(setSettingsMenuOpen)}/>
      <AddNodeDrawer open={addNodeOpen} toggleDrawer={toggle(setAddNodeOpen)}/>
      <ShareNodeDrawer open={shareNodeOpen} toggleDrawer={toggle(setShareNodeOpen)}/>
      <Zoom
        key="add-node-fab-zoom"
        in={navigatorOpen}
        timeout={transitionDuration}
        style={{
          transitionDelay: `${navigatorOpen ? transitionDuration.exit : 0}ms`,
        }}
        unmountOnExit
      >
        <Fab
          color="secondary"
          aria-label="add item"
          size={"small"}
          title={"add a new item to the tree"}
          onClick={toggle(setAddNodeOpen)(addNodeOpen)}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            position: "fixed",
            left: 0,
            bottom: 0,
            margin: 1.25,
          }}
        >
          <AddIcon fontSize="large"/>
        </Fab>
      </Zoom>
      <Box
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          position: "fixed",
          left: 0,
          bottom: 0,
          transition: theme.transitions.create("left", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.getAutoHeightDuration(exitSpeedFactor),
          }),
          ...((notMobile && navigatorOpen) && {
            left: `max(${drawerWidth}, ${standardMinWidth}px)`,
            transition: theme.transitions.create("left", {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.getAutoHeightDuration(enterSpeedFactor),
            }),
          }),
        }}
      >
        <Zoom
          in={!navigatorOpen || notMobile}
          timeout={transitionDuration}
          style={{
            transitionDelay: `${!navigatorOpen ? transitionDuration.exit : 0}ms`,
          }}
          unmountOnExit
        >
          <Fab
            color="primary"
            aria-label="add item"
            size={"small"}
            title={"add a new item to the tree"}
            onClick={() => navigate("/editing")}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 2,
              margin: 1.25,
            }}
          >
            <EditIcon/>
          </Fab>
        </Zoom>
      </Box>
      <Fab
        color="secondary"
        aria-label="share item"
        size={"small"}
        title={"share current item with others"}
        onClick={toggle(setShareNodeOpen)(shareNodeOpen)}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          position: "fixed",
          right: 0,
          bottom: 0,
          margin: 1.25,
        }}
      >
        <ShareIcon/>
      </Fab>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          padding: `0 ${theme.spacing(3)} 0 ${theme.spacing(3)}`,
          width: notMobile ? "auto" : "100vw",
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.getAutoHeightDuration(exitSpeedFactor),
          }),
          ...(navigatorOpen && {
            marginLeft: `max(${drawerWidth}, ${standardMinWidth}px)`,
            transition: theme.transitions.create("margin", {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.getAutoHeightDuration(enterSpeedFactor),
            }),
          }),
        }}>
        <Toolbar variant="dense"/> {/*to add spacing for header*/}
        {React.Children.map(children, child => React.cloneElement(child, {setTitle, setCopyrightOwnership}))}
        {(copyrightOwnership) ? <Footer copyrightOwnership={copyrightOwnership}/> : ""}
      </Box>
    </>
  )
}

export default Layout
